import React from "react";
import { Stack, useMantineTheme } from "@mantine/core";
import { TumblingEObject, Direction, SnellenSize, TumblingEStatus } from "types/testTypes";

const orientationRotationMap: Record<Direction, number> = {
  [Direction.RIGHT]: 0,
  [Direction.DOWN]: 90,
  [Direction.LEFT]: 180,
  [Direction.UP]: 270,
};

const snellenScaleMap: Record<SnellenSize, number> = {
  [SnellenSize.SIZE_20_200]: 5,
  [SnellenSize.SIZE_20_100]: 2.5,
  [SnellenSize.SIZE_20_80]: 2,
  [SnellenSize.SIZE_20_63]: 1.5,
  [SnellenSize.SIZE_20_50]: 1.25,
  [SnellenSize.SIZE_20_40]: 1,
  [SnellenSize.SIZE_20_32]: 0.75,
  [SnellenSize.SIZE_20_25]: 0.6,
  [SnellenSize.SIZE_20_20]: 0.5,
};

export interface TumblingEViewerProps {
  tumblingE: TumblingEObject;
  baseSizePx?: number;
  className?: string;
  style?: React.CSSProperties;
  colorOverride?: string;
}

/**
 * TumblingEViewer
 * Renders a single Tumbling E figure rotated & scaled based on orientation & Snellen size.
 * Shows an optional status indicator beneath the figure.
 */
export const TumblingEViewer: React.FC<TumblingEViewerProps> = ({
  tumblingE,
  baseSizePx = 100,
  className,
  style,
  colorOverride,
}) => {
  const theme = useMantineTheme();

  if (!tumblingE) return null;

  const rotation = orientationRotationMap[tumblingE.orientation] ?? 0;
  const scale = snellenScaleMap[tumblingE.snellenSize] ?? 1;

  const statusColor =
    colorOverride ||
    (() => {
      switch (tumblingE.status) {
        case TumblingEStatus.CURRENTLY_DISPLAYED:
          return theme.colors.blue?.[6] || "#228be6";
        case TumblingEStatus.CORRECTLY_RESPONDED:
          return theme.colors.green?.[6] || "#40c057";
        case TumblingEStatus.INCORRECTLY_RESPONDED:
          return theme.colors.red?.[6] || "#fa5252";
        case TumblingEStatus.SKIPPED:
          return theme.colors.orange?.[6] || "#fd7e14";
        case TumblingEStatus.HIDDEN:
        default:
          return theme.colors.gray?.[5] || "#adb5bd";
      }
    })();

  // The viewer’s size scales with Snellen size so layout adapts naturally
  const sizedPx = Math.max(1, Math.round(baseSizePx * scale));

  return (
    <Stack align="center" gap={4} className={className} style={style}>
      <svg
        viewBox="0 0 5 5"
        width={sizedPx}
        height={sizedPx}
        style={{
          transform: `rotate(${rotation}deg)`,
          transformOrigin: "center center",
          fill: statusColor,
          display: "block",
        }}
      >
        <rect x="0" y="0" width="1" height="5" />
        <rect x="0" y="0" width="5" height="1" />
        <rect x="0" y="2" width="5" height="1" />
        <rect x="0" y="4" width="5" height="1" />
      </svg>
    </Stack>
  );
};

export default TumblingEViewer;
